import { useEffect } from "react";
import { useTeamStore, resolvePermissionsForOwner, roleForOwner } from "../Store/teamStore";
import decodeToken from "../lib/decodeToken";

// Full rights on agents you own yourself — no team restriction ever applies.
const OWN_AGENT_PERMISSIONS = {
  viewAgents: true,
  editAgents: true,
  dialer: true,
  viewCallLogs: true,
  viewAppointments: true,
  viewContacts: true,
  manageBilling: true,
};


/**
 * useAgentPermissions — perff-agent, per-owner permission resolution.
 *
 * In the multi-owner model a team member may be an ADMIN on one owner's agents
 * and a view-only SUPPORT member on another's. Global permissions no longer
 * make sense — capabilities depend on WHICH owner owns the agent in front of
 * you. This hook resolves that.
 *
 * Pass the agent object (or an ownerId). The agent's owner is read from
 * `agent.__ownerId` (tagged by fetchTeamDashboard) or `agent.userId` (raw DB
 * row). Account owners (not team members) always get full access.
 *
 * @param {object|string} agentOrOwnerId  agent object or ownerId string
 */
const useAgentPermissions = (agentOrOwnerId) => {
  const teams = useTeamStore((s) => s.teams);
  const isTeamMember = useTeamStore((s) => s.isTeamMember);
  const loaded = useTeamStore((s) => s.loaded);
  const loadTeams = useTeamStore((s) => s.loadTeams);

  const isTeam = isTeamMember || !!localStorage.getItem("teamOwnerId");

  // Ensure memberships are loaded once for team members.
  useEffect(() => {
    if (isTeam && !loaded) loadTeams();
  }, [isTeam, loaded, loadTeams]);

  const ownerId =
    typeof agentOrOwnerId === "string"
      ? agentOrOwnerId
      : agentOrOwnerId?.__ownerId || agentOrOwnerId?.userId || null;

  // A team member can ALSO own agents (admins may create their own). Those sit
  // under their own userId and never appear in the memberships list, so resolve
  // them as full owner access — never restrict someone on their own agent.
  const selfId = decodeToken(localStorage.getItem("token") || "")?.id || null;
  const isOwnAgent = !!ownerId && !!selfId && ownerId === selfId;

  // Prefer permissions already tagged onto the agent (dashboard fan-out);
  // otherwise resolve from the loaded memberships by owner.
  const tagged =
    typeof agentOrOwnerId === "object" ? agentOrOwnerId?.__permissions : null;
  const permissions = isOwnAgent
    ? OWN_AGENT_PERMISSIONS
    : tagged && Object.keys(tagged).length
      ? tagged
      : resolvePermissionsForOwner(teams, isTeam, ownerId);

  const role = isOwnAgent
    ? "owner"
    : typeof agentOrOwnerId === "object" && agentOrOwnerId?.__role
      ? agentOrOwnerId.__role
      : roleForOwner(teams, isTeam, ownerId);

  const isAdmin = !isTeam || role === "admin" || role === "owner";

  return {
    isTeamMember: isTeam,
    ownerId,
    role,
    isAdmin,
    canEditAgents: !isTeam || isAdmin || !!permissions?.editAgents,
    canAccessDialer: !isTeam || isAdmin || !!permissions?.dialer,
    canViewCallLogs: !isTeam || isAdmin || permissions?.viewCallLogs !== false,
    canViewAppointments: !isTeam || isAdmin || permissions?.viewAppointments !== false,
    // Contacts: admins always have access; support is locked by default.
    canViewContacts: !isTeam || isAdmin || !!permissions?.viewContacts,
    canManageBilling: !isTeam || isAdmin || !!permissions?.manageBilling,
    permissions,
  };
};

export default useAgentPermissions;
